import { useState, useEffect, useCallback } from 'react';
import { axiosInstance } from '@/App';
import { startOfMonth, endOfMonth, format } from 'date-fns';

/**
 * Hook for fetching attendance history with filtering and pagination.
 *
 * @param {Object} initialFilters - Optional initial filter values
 * @returns {Object} Attendance history state and methods
 */
export function useAttendanceHistory(initialFilters = {}) {
  const [entries, setEntries] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [totalCount, setTotalCount] = useState(0);

  // Default to current month
  const [filters, setFilters] = useState({
    from_date: format(startOfMonth(new Date()), 'yyyy-MM-dd'),
    to_date: format(endOfMonth(new Date()), 'yyyy-MM-dd'),
    gym_id: null,
    status: null,
    page: 1,
    page_size: 20,
    ...initialFilters
  });

  const fetchHistory = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const params = {
        from_date: filters.from_date,
        to_date: filters.to_date,
        page: filters.page,
        page_size: filters.page_size
      };

      if (filters.gym_id) params.gym_id = filters.gym_id;
      if (filters.status) params.status = filters.status;

      const response = await axiosInstance.get('/attendance/daily/my-history', { params });
      setEntries(response.data.entries || []);
      setSummary(response.data.summary || null);
      setTotalCount(response.data.total_count || 0);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load attendance history');
      console.error('Attendance history error:', err);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  const updateFilters = (newFilters) => {
    setFilters(prev => ({ ...prev, ...newFilters, page: 1 }));
  };

  const setPage = (page) => {
    setFilters(prev => ({ ...prev, page }));
  };

  return {
    entries,
    summary,
    loading,
    error,
    totalCount,
    filters,
    updateFilters,
    setPage,
    refetch: fetchHistory
  };
}
